import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Trash2, ShieldCheck } from "lucide-react";

const KINDS = [
  { value: "public_liability_insurance", label: "Public liability insurance" },
  { value: "trade_licence", label: "Trade licence" },
  { value: "supervisor_card", label: "Supervisor card" },
  { value: "other", label: "Other" },
];

export default function PainterCredentials() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ kind: "public_liability_insurance", issuer: "", reference: "", expires_on: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const r = await api.get("/painter/certificates"); setItems(r.data.items || []); }
    catch (e) {
      if (e?.response?.status === 404) { setItems([]); return; }
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !form.issuer || !form.reference) { toast.error("Issuer, reference and file are required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("kind", form.kind); fd.append("issuer", form.issuer);
      fd.append("reference", form.reference);
      if (form.expires_on) fd.append("expires_on", form.expires_on);
      await api.post("/painter/certificates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ kind: form.kind, issuer: "", reference: "", expires_on: "" });
      if (fileRef.current) fileRef.current.value = "";
      await load(); toast.success("Certificate added");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this certificate?")) return;
    try { await api.delete(`/painter/certificates/${id}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="painter-credentials" className="space-y-8">
      <div>
        <div className="overline mb-2">Credentials</div>
        <h1 className="font-serif text-3xl text-brand-ink mb-1">Licences and insurance</h1>
        <p className="text-sm text-brand-muted">Upload your current documents. Our team verifies each one before awarding the Verified badge.</p>
      </div>

      <div className="border border-brand-linen p-5 bg-white space-y-3" data-testid="cert-form">
        <select data-testid="cert-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
          className="border border-brand-linen p-3 text-sm w-full">
          {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
        <input data-testid="cert-issuer" placeholder="Issuer (e.g. QBE Insurance)"
          value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })}
          className="border border-brand-linen p-3 text-sm w-full" />
        <input data-testid="cert-reference" placeholder="Reference / policy number"
          value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
          className="border border-brand-linen p-3 text-sm w-full" />
        <input data-testid="cert-expires" type="date" value={form.expires_on}
          onChange={(e) => setForm({ ...form, expires_on: e.target.value })}
          className="border border-brand-linen p-3 text-sm w-full" />
        <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
          data-testid="cert-file" className="text-sm" />
        <button onClick={upload} disabled={busy} data-testid="cert-upload"
          className="px-5 py-2.5 bg-brand-navy text-white text-sm disabled:opacity-50">
          {busy ? "Uploading…" : "Add certificate"}
        </button>
      </div>

      <ul className="divide-y border border-brand-linen bg-white" data-testid="cert-list">
        {items.length === 0 && <li className="p-4 text-sm text-brand-muted">No credentials uploaded yet.</li>}
        {items.map((c) => (
          <li key={c.id} className="flex items-center justify-between p-4" data-testid={`cert-${c.id}`}>
            <div>
              <div className="text-sm text-brand-ink flex items-center gap-2">
                {c.kind.replace(/_/g, " ")} {c.verified && <ShieldCheck className="w-4 h-4 text-brand-teal" />}
              </div>
              <div className="text-xs text-brand-muted">
                {c.issuer} · ref {c.reference} {c.expires_on && `· expires ${c.expires_on}`}
              </div>
            </div>
            <button onClick={() => remove(c.id)} data-testid={`cert-remove-${c.id}`}
              className="p-1.5 text-brand-muted hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
